import * as XLSX from "xlsx-js-style";

const COLOR_DATOS = "1F6FA8";
const COLOR_INSUMOS = "548235";
const COLOR_GUIA = "595959";
const COLOR_EJEMPLO = "C55A11";
const FILAS_INSUMOS = 40;

const aclararColor = (hex, factor) => {
  const canal = (inicio) => parseInt(hex.substring(inicio, inicio + 2), 16);
  const mezclar = (c) => Math.round(c + (255 - c) * factor).toString(16).padStart(2, "0").toUpperCase();
  return `${mezclar(canal(0))}${mezclar(canal(2))}${mezclar(canal(4))}`;
};

const BORDE = {
  top: { style: "thin", color: { rgb: "C0C0C0" } },
  bottom: { style: "thin", color: { rgb: "C0C0C0" } },
  left: { style: "thin", color: { rgb: "C0C0C0" } },
  right: { style: "thin", color: { rgb: "C0C0C0" } },
};

const estiloBanner = (color) => ({
  fill: { fgColor: { rgb: color } },
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11, name: "Arial" },
  alignment: { horizontal: "center", vertical: "center" },
  border: BORDE,
});
const estiloEtiqueta = (color) => ({
  ...estiloBanner(color),
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Arial" },
  alignment: { horizontal: "left", vertical: "center" },
});
const estiloEncabezadoColumna = (color) => ({
  fill: { fgColor: { rgb: aclararColor(color, 0.75) } },
  font: { bold: true, sz: 10, name: "Arial" },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: BORDE,
});
const estiloCelda = () => ({
  font: { sz: 10, name: "Arial" },
  alignment: { vertical: "center" },
  border: BORDE,
});

const aplicarEstilo = (ws, fila, columna, estilo) => {
  const ref = XLSX.utils.encode_cell({ r: fila, c: columna });
  if (!ws[ref]) ws[ref] = { t: "s", v: "" };
  ws[ref].s = estilo;
};

const CAMPOS_MATRIZ = [
  ["Clave", "Descripción"],
  ["Unidad", "Familia"],
  ["Tipo", "Permitir venta"],
  ["Descripción larga", "Código de barras"],
  ["Tipo de insumo", "Código SAT"],
  ["Impuesto 1", "Impuesto 2"],
  ["Retención 1", "Retención 2"],
];
const ENCABEZADO_INSUMOS = ["Clave insumo", "Nombre", "Unidad", "Cantidad"];

const construirHojaMatriz = (datos, partidas, color, filasVacias) => {
  const aoa = CAMPOS_MATRIZ.map(([izquierda, derecha]) => [izquierda, datos[izquierda] ?? "", derecha, datos[derecha] ?? ""]);
  aoa.push(ENCABEZADO_INSUMOS);
  partidas.forEach((p) => aoa.push([p.clave, p.nombre, p.unidad, p.cantidad]));
  for (let i = partidas.length; i < filasVacias; i++) aoa.push(["", "", "", ""]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{ wch: 24 }, { wch: 44 }, { wch: 24 }, { wch: 44 }];
  ws["!rows"] = aoa.map((_, i) => ({ hpt: i <= CAMPOS_MATRIZ.length ? 22 : 18 }));

  const filaEncabezado = CAMPOS_MATRIZ.length;
  for (let f = 0; f < filaEncabezado; f++) {
    aplicarEstilo(ws, f, 0, estiloEtiqueta(COLOR_DATOS));
    aplicarEstilo(ws, f, 1, estiloCelda());
    aplicarEstilo(ws, f, 2, estiloEtiqueta(COLOR_DATOS));
    aplicarEstilo(ws, f, 3, estiloCelda());
  }
  for (let c = 0; c < 4; c++) aplicarEstilo(ws, filaEncabezado, c, estiloEncabezadoColumna(color));
  for (let f = filaEncabezado + 1; f < aoa.length; f++) {
    for (let c = 0; c < 4; c++) aplicarEstilo(ws, f, c, estiloCelda());
  }
  return ws;
};

const hojaListado = (encabezados, filas, anchos, color) => {
  const ws = XLSX.utils.aoa_to_sheet([encabezados, ...filas]);
  ws["!cols"] = anchos.map((wch) => ({ wch }));
  encabezados.forEach((_, c) => aplicarEstilo(ws, 0, c, estiloBanner(color)));
  return ws;
};

const texto = (valor) => (valor === undefined || valor === null ? "" : String(valor).trim());
const esSi = (valor) => ["si", "sí"].includes(texto(valor).toLowerCase());

export const leerPlantillaMatriz = async (archivo, catalogos) => {
  const buffer = await archivo.arrayBuffer();
  const libro = XLSX.read(new Uint8Array(buffer), { type: "array" });
  const hoja = libro.Sheets[libro.SheetNames[0]];
  const filas = XLSX.utils.sheet_to_json(hoja, { header: 1, defval: "", blankrows: true });
  const errores = [];

  const indiceEncabezado = filas.findIndex((fila) => texto(fila[0]) === ENCABEZADO_INSUMOS[0]);
  if (indiceEncabezado === -1) {
    return { errores: ["No se encontró el encabezado \"Clave insumo\". Use la plantilla descargada."], matriz: null };
  }

  const valores = {};
  const filaDeCampo = {};
  for (let i = 0; i < indiceEncabezado; i++) {
    [[0, 1], [2, 3]].forEach(([columnaEtiqueta, columnaValor]) => {
      const etiqueta = texto(filas[i][columnaEtiqueta]);
      if (etiqueta) {
        valores[etiqueta] = texto(filas[i][columnaValor]);
        filaDeCampo[etiqueta] = i + 1;
      }
    });
  }

  const clave = valores["Clave"];
  const descripcion = valores["Descripción"];
  const venta = esSi(valores["Permitir venta"]);
  const descripcionLarga = valores["Descripción larga"] || "";
  const codigoBarras = valores["Código de barras"] || "";
  const codigoSat = valores["Código SAT"] || "";

  if (!clave) errores.push(`Fila ${filaDeCampo["Clave"]}: falta la Clave`);
  if (!descripcion) errores.push(`Fila ${filaDeCampo["Descripción"]}: falta la Descripción`);
  const unidad = catalogos.unidades.find((u) => texto(u.nombre) === valores["Unidad"]);
  if (!unidad) errores.push(`Fila ${filaDeCampo["Unidad"]}: la Unidad "${valores["Unidad"] || ""}" no existe`);
  const familia = catalogos.familias.find((f) => texto(f.nombre) === valores["Familia"]);
  if (!familia) errores.push(`Fila ${filaDeCampo["Familia"]}: la Familia "${valores["Familia"] || ""}" no existe`);
  const tipoMatriz = { base: 1, final: 2 }[(valores["Tipo"] || "").toLowerCase()];
  if (!tipoMatriz) errores.push(`Fila ${filaDeCampo["Tipo"]}: el Tipo debe ser Base o Final`);

  let tipoDeInsumoId;
  if (valores["Tipo de insumo"]) {
    const tipoDeInsumo = (catalogos.tiposDeInsumo || []).find((t) => texto(t.nombre) === valores["Tipo de insumo"]);
    if (tipoDeInsumo) tipoDeInsumoId = tipoDeInsumo._id;
    else errores.push(`Fila ${filaDeCampo["Tipo de insumo"]}: el Tipo de insumo "${valores["Tipo de insumo"]}" no existe`);
  }
  if (codigoSat && !/^\d{8}$/.test(codigoSat)) {
    errores.push(`Fila ${filaDeCampo["Código SAT"]}: el Código SAT debe tener 8 dígitos`);
  }

  const resolverLista = (etiquetas, lista, nombreCatalogo) => {
    const ids = [];
    etiquetas.forEach((etiqueta) => {
      if (!valores[etiqueta]) return;
      const encontrado = (lista || []).find((item) => texto(item.nombre) === valores[etiqueta]);
      if (!encontrado) errores.push(`Fila ${filaDeCampo[etiqueta]}: ${etiqueta} "${valores[etiqueta]}" no existe en ${nombreCatalogo}`);
      else if (ids.includes(encontrado._id)) errores.push(`Fila ${filaDeCampo[etiqueta]}: ${etiqueta} está repetido`);
      else ids.push(encontrado._id);
    });
    return ids;
  };
  const impuestos = resolverLista(["Impuesto 1", "Impuesto 2"], catalogos.impuestos, "Impuestos");
  const retenciones = resolverLista(["Retención 1", "Retención 2"], catalogos.retenciones, "Retenciones");
  const nombresImpuestos = impuestos.map((id) => catalogos.impuestos.find((i) => i._id === id)?.nombre);
  const nombresRetenciones = retenciones.map((id) => catalogos.retenciones.find((r) => r._id === id)?.nombre);

  const porClave = new Map([...catalogos.insumos, ...catalogos.matricesBase].map((i) => [texto(i.clave), i]));
  const vistas = new Set();
  const partidas = [];
  for (let i = indiceEncabezado + 1; i < filas.length; i++) {
    const claveInsumo = texto(filas[i][0]);
    const cantidadCruda = texto(filas[i][3]);
    if (!claveInsumo && !cantidadCruda) continue;
    const numeroFila = i + 1;
    const insumo = porClave.get(claveInsumo);
    const cantidad = Number(cantidadCruda.replace(/,/g, ""));
    if (!insumo) {
      errores.push(`Fila ${numeroFila}: la clave "${claveInsumo}" no está en Insumos ni en Matrices Base`);
    } else if (vistas.has(claveInsumo)) {
      errores.push(`Fila ${numeroFila}: la clave "${claveInsumo}" está repetida`);
    } else if (!cantidadCruda || isNaN(cantidad) || cantidad <= 0) {
      errores.push(`Fila ${numeroFila}: la Cantidad debe ser un número mayor a cero`);
    } else {
      vistas.add(claveInsumo);
      partidas.push({ insumoId: insumo._id, cantidad, clave: claveInsumo, nombre: insumo.descripcion, unidad: insumo.unidad });
    }
  }
  if (partidas.length === 0 && errores.length === 0) errores.push(`No hay insumos desde la fila ${indiceEncabezado + 2}`);

  return {
    errores,
    matriz: {
      clave,
      descripcion,
      descripcionLarga,
      codigoBarras,
      codigoSat,
      tipoDeInsumoId,
      tipoDeInsumo: valores["Tipo de insumo"] || "",
      unidadId: unidad?._id,
      familiaId: familia?._id,
      tipoMatriz,
      venta,
      impuestos,
      retenciones,
      nombresImpuestos,
      nombresRetenciones,
      partidas,
    },
  };
};

export const descargarPlantillaMatriz = (catalogos) => {
  const { unidades = [], familias = [], insumos = [], matricesBase = [], impuestos = [], retenciones = [], tiposDeInsumo = [] } = catalogos;
  const wb = XLSX.utils.book_new();

  const hojaPrincipal = construirHojaMatriz(
    { "Permitir venta": "NO" },
    [],
    COLOR_INSUMOS,
    FILAS_INSUMOS
  );
  XLSX.utils.book_append_sheet(wb, hojaPrincipal, "Matriz");

  const instrucciones = [
    ["Instrucciones de uso"],
    [""],
    ["1. Hoja \"Matriz\", filas 1 a 7: datos principales de la matriz. Llene solo las celdas de la derecha de cada etiqueta (azul)."],
    ["2. Obligatorios: Clave, Descripción, Unidad, Familia y Tipo. El resto es opcional y se puede dejar vacío."],
    ["3. Hoja \"Matriz\", fila 8: encabezados de los insumos. A partir de la fila 9 capture un insumo por fila: Clave insumo, Nombre, Unidad y Cantidad que lleva en la matriz."],
    ["4. La Clave insumo debe existir en la hoja \"Insumos\" o en la hoja \"Matrices Base\". El Nombre y la Unidad deben coincidir con los que aparecen ahí."],
    ["5. Unidad, Familia, Tipo de insumo, Impuesto 1 y 2, Retención 1 y 2: escriba el nombre exacto como aparece en la hoja \"Unidades\", \"Familias\", \"Tipos de Insumo\", \"Impuestos\" o \"Retenciones\"."],
    ["6. Tipo: escriba Base (se usa para componer otras matrices) o Final (se usa directamente en un presupuesto)."],
    ["7. Permitir venta: escriba SI o NO."],
    ["8. Código SAT: 8 dígitos, por ejemplo 31201600."],
    ["9. Cantidad: número mayor a cero. Las cantidades siempre van en la unidad del insumo."],
    ["10. No borre ni mueva las filas de encabezado. Puede agregar más filas de insumos hacia abajo."],
    ["11. La hoja \"Ejemplo\" muestra una matriz ya llena con datos reales de su empresa."],
  ];
  const wsInstr = XLSX.utils.aoa_to_sheet(instrucciones);
  wsInstr["!cols"] = [{ wch: 120 }];
  aplicarEstilo(wsInstr, 0, 0, estiloBanner(COLOR_GUIA));
  XLSX.utils.book_append_sheet(wb, wsInstr, "Instrucciones");

  const muestraInsumos = [...insumos.slice(0, 3)];
  while (muestraInsumos.length < 3) {
    muestraInsumos.push({ clave: `INS-00${muestraInsumos.length + 1}`, descripcion: "Nombre del insumo", unidad: "Pieza" });
  }
  const hojaEjemplo = construirHojaMatriz(
    {
      "Clave": "MAT-001",
      "Descripción": "Kit de ejemplo",
      "Unidad": unidades[0]?.nombre || "Pieza",
      "Familia": familias[0]?.nombre || "General",
      "Tipo": "Final",
      "Permitir venta": "SI",
      "Descripción larga": "Kit de ejemplo armado con tres insumos",
      "Código de barras": "7501234567890",
      "Tipo de insumo": tiposDeInsumo[0]?.nombre || "",
      "Código SAT": "31201600",
      "Impuesto 1": impuestos[0]?.nombre || "",
      "Impuesto 2": impuestos[1]?.nombre || "",
      "Retención 1": retenciones[0]?.nombre || "",
      "Retención 2": retenciones[1]?.nombre || "",
    },
    muestraInsumos.map((insumo, i) => ({
      clave: insumo.clave,
      nombre: insumo.descripcion,
      unidad: insumo.unidad,
      cantidad: [2, 1.5, 10][i],
    })),
    COLOR_EJEMPLO,
    0
  );
  XLSX.utils.book_append_sheet(wb, hojaEjemplo, "Ejemplo");

  XLSX.utils.book_append_sheet(
    wb,
    hojaListado(
      ["Clave", "Nombre", "Unidad"],
      insumos.map((i) => [i.clave, i.descripcion, i.unidad]),
      [24, 60, 20],
      COLOR_INSUMOS
    ),
    "Insumos"
  );
  XLSX.utils.book_append_sheet(
    wb,
    hojaListado(
      ["Clave", "Nombre", "Unidad"],
      matricesBase.map((i) => [i.clave, i.descripcion, i.unidad]),
      [24, 60, 20],
      COLOR_DATOS
    ),
    "Matrices Base"
  );
  XLSX.utils.book_append_sheet(
    wb,
    hojaListado(["Nombre", "Descripción"], unidades.map((u) => [u.nombre, u.descripcion || ""]), [24, 50], COLOR_GUIA),
    "Unidades"
  );
  XLSX.utils.book_append_sheet(
    wb,
    hojaListado(["Nombre", "Descripción"], familias.map((f) => [f.nombre, f.descripcion || ""]), [30, 50], COLOR_GUIA),
    "Familias"
  );
  XLSX.utils.book_append_sheet(
    wb,
    hojaListado(["Nombre"], tiposDeInsumo.map((t) => [t.nombre]), [40], COLOR_GUIA),
    "Tipos de Insumo"
  );
  XLSX.utils.book_append_sheet(
    wb,
    hojaListado(["Nombre"], impuestos.map((i) => [i.nombre]), [40], COLOR_GUIA),
    "Impuestos"
  );
  XLSX.utils.book_append_sheet(
    wb,
    hojaListado(["Nombre"], retenciones.map((r) => [r.nombre]), [40], COLOR_GUIA),
    "Retenciones"
  );

  XLSX.writeFile(wb, "plantillaMatriz.xlsx");
};
